import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import {
  UseFormRegister,
  UseFormSetValue,
  FieldErrors,
  UseFormClearErrors,
} from "react-hook-form";
import {
  Box,
  Typography,
  Button,
  useTheme,
  alpha,
  FormHelperText,
} from "@mui/material";
import { FaUpload, FaSpinner, FaFile } from "react-icons/fa6";
import {
  useUploadSingleFileMutation,
  useDeleteSingleFileMutation,
} from "../../Services/ProductService";
import { showSnackbar } from "../../Redux/Slices/NotificationSlice";
import { useDispatch } from "react-redux";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// import Utils from '../../Helpers/Utils';
const ImageCropper = lazy(() => import("./ImageCropper"));

interface FileUploaderProps {
  register: UseFormRegister<any>;
  setValue: (value: string) => void;
  clearErrors: UseFormClearErrors<any>;
  errors: FieldErrors<any>;
  label?: React.ReactNode;
  keyName?: string;
  value?: string;
  onChange?: (value: string) => void;
  isCrop?: boolean;
  cropWidth?: number;
  cropHeight?: number;
  minWidth?: number;
  minHeight?: number;
  maxFileSizeKB?: number;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  register,
  setValue,
  errors,
  label,
  keyName = "productImage",
  value,
  clearErrors,
  onChange,
  isCrop = false,
  cropWidth = 1920,
  cropHeight = 400,
  minWidth,
  minHeight,
  maxFileSizeKB,
}) => {
  const theme = useTheme();
  const [logoName, setLogoName] = useState<string | null>(null);
  const [isLoadingImg, setIsLoadingImg] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tempS3File = useRef<string>("");
  const [isDragActive, setIsDragActive] = useState(true);
  const [openImageCropper, setOpenImageCropper] = useState<any>({
    open: false,
    selectedValue: "",
  });
  const [previewImage, setPreviewImage] = useState<any>();
  const [Key, setKey] = useState<string>("");
  const [isCopied, setIsCopied] = useState(false);
  const dispatch = useDispatch();
  const [triggerSingleFileUpload] = useUploadSingleFileMutation();
  const [deleteSingleFile] = useDeleteSingleFileMutation();

  const validateFile = async (file: File): Promise<true | string> => {
    return new Promise((resolve) => {
      if (!file) {
        return resolve("File is required");
      }

      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        return resolve("Only JPEG, PNG, and WEBP files are allowed");
      }

      const maxBytes = maxFileSizeKB ? maxFileSizeKB * 1024 : 10 * 1024 * 1024;
      if (file.size > maxBytes) {
        const label = maxFileSizeKB ? `${maxFileSizeKB}KB` : "10MB";
        return resolve(`File size must be less than ${label}`);
      }

      if (isCrop) {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (e) => {
          if (!e.target?.result) {
            return resolve("Could not read file");
          }

          img.src = e.target.result as string;

          img.onload = () => {
            const reqW = minWidth || cropWidth;
            const reqH = minHeight || cropHeight;
            if (img.width < reqW || img.height < reqH) {
              return resolve(
                `Image size is too small. Minimum required: ${reqW}x${reqH} pixels. Current: ${img.width}x${img.height} pixels.`,
              );
            }
            return resolve(true);
          };

          img.onerror = () => {
            resolve("Invalid image file");
          };
        };

        reader.readAsDataURL(file);
      } else {
        return resolve(true);
      }
    });
  };

  useEffect(() => {
    if (!value) {
      // Clear internal state if value is cleared
      setLogoName(null);
      setPreviewImage(null);
    } else {
      setLogoName(value);
      setPreviewImage(value);
    }
  }, [value]);

  const handleFile = async (file: File) => {
    const validationResult = await validateFile(file);
    if (validationResult === true) {
      if (isCrop) {
        setOpenImageCropper({ open: true, selectedValue: file });
      } else {
        finallyUploadFile(file);
      }
    } else {
      setValue("");
      dispatch(
        showSnackbar({
          message: `${validationResult}`,
          severity: "error",
        }),
      );
      if (onChange) {
        onChange("");
      }
    }
  };

  const finallyUploadFile = async (file: File) => {
    setIsLoadingImg(true);
    setLogoName(file.name);
    try {
      if (file) {
        const formData = new FormData();
        formData.append("file", file);

        const result = await triggerSingleFileUpload(formData).unwrap();

        if (result?.data?.url) {
          tempS3File.current = result.data?.url;
          setKey(result?.data?.recordId);
          setValue(result.data?.url);
          setPreviewImage(result.data?.url);
          clearErrors(keyName);
          if (onChange) {
            onChange(result.data?.url);
          }
        }
      }
    } catch (error: any) {
      setLogoName(null);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        (typeof error?.data === "string" ? error.data : null) ||
        "Failed to upload file";
      dispatch(
        showSnackbar({
          message: errorMessage,
          severity: "error",
        }),
      );
    }
    setIsLoadingImg(false);
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleFile(file);
    }
  };

  const deleteFile = async () => {
    try {
      const payload = Key || value || tempS3File.current;
      if (payload) {
        // Prefer recordId if present, otherwise use the URL query param
        await deleteSingleFile(payload).unwrap();
        tempS3File.current = "";
      }
      setLogoName(null);
      setValue("");
      if (onChange) {
        onChange("");
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {}
  };

  const handleChangeFile = () => {
    // setLogoName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const HandleCroped = (value: boolean, croppedFile?: File) => {
    if (croppedFile) {
      setOpenImageCropper({ open: false, selectedValue: croppedFile });
      finallyUploadFile(croppedFile);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(previewImage);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
  };

  return (
    <Box sx={{ position: "relative", fontSize: "0.75rem" }}>
      {isLoadingImg && (
        <Box
          sx={{
            position: "absolute",
            zIndex: 10,
            mt: "12px",
            ml: "16px",
            px: 1,
            py: 0.5,
            bgcolor: "background.paper",
            color: "text.secondary",
            fontSize: "0.75rem",
            borderRadius: 1,
          }}
        >
          Image loading please wait...
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label || "Product Image"}
      </Typography>

      <Box
        sx={{
          border: `1px dashed ${
            isDragActive
              ? theme.palette.primary.main
              : errors[keyName]
                ? theme.palette.error.main
                : logoName
                  ? theme.palette.success.main
                  : theme.palette.error.main
          }`,
          borderRadius: 1,
          p: 2,
          textAlign: "center",
          backgroundColor: isDragActive
            ? alpha(theme.palette.primary.main, 0.1)
            : errors[keyName]
              ? alpha(theme.palette.error.main, 0.1)
              : theme.palette.background.default,
          cursor: logoName ? "default" : "pointer",
          "&:hover": {
            backgroundColor: logoName
              ? "inherit"
              : alpha(theme.palette.primary.main, 0.1),
          },
        }}
        onClick={() => !logoName && fileInputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragActive(false);
        }}
        onDrop={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragActive(false);
          const files = e.dataTransfer.files;
          if (files.length > 0) {
            const file = files[0];
            await handleFile(file);
          }
        }}
      >
        {logoName ? (
          <Box sx={{ mb: 2 }}>
            <img
              src={`${previewImage}`}
              alt="Preview"
              style={{
                maxWidth: "200px",
                maxHeight: "200px",
                objectFit: "contain",
                borderRadius: "4px",
              }}
            />
          </Box>
        ) : (
          <FaUpload size={24} color={theme.palette.text.secondary} />
        )}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/png, image/jpeg, image/webp, image/jpg"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {logoName
              ? "File selected"
              : isDragActive
                ? "Drop file here"
                : "Drag your file(s) or browse"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {logoName
              ? "Use buttons below to copy URL or delete Image"
              : `Max ${maxFileSizeKB ? `${maxFileSizeKB}KB` : "10MB"} files are allowed`}
          </Typography>
        </Box>
      </Box>

      {isLoadingImg && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "160px",
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <FaSpinner
              className="animate-spin"
              style={{
                width: "48px",
                height: "48px",
                color: theme.palette.primary.main,
                marginBottom: theme.spacing(1),
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Uploading...
            </Typography>
            <Button
              size="small"
              type={"button"}
              variant="contained"
              color="error"
              onClick={deleteFile}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {logoName && !isLoadingImg && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            p: 2,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FaFile
              style={{
                width: "20px",
                height: "20px",
                color: theme.palette.primary.main,
                marginRight: theme.spacing(1),
              }}
            />
            <Typography variant="body2">{logoName}</Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            {/* <Button
              type={'button'}
              size="small"
              variant="outlined"
              sx={{ mr: 1, fontSize: 12 }}
              onClick={handleChangeFile}

            >
              Change&nbsp;File
            </Button> */}
            {/* <Button
              type={'button'}
              size="small"
              variant="outlined"
              sx={{ mr: 1, fontSize: 12 }}
              onClick={handleCopyUrl}

            >
              Copy&nbsp;URL
            </Button> */}
            <Button
              type="button"
              size="small"
              variant="outlined"
              sx={{ mr: 1, fontSize: 12, gap: 1 }}
              onClick={handleCopyUrl}
            >
              {isCopied ? (
                <>
                  <CheckIcon fontSize="small" /> Copied
                </>
              ) : (
                <>
                  <ContentCopyIcon fontSize="small" /> Copy URL
                </>
              )}
            </Button>

            <Button
              size="small"
              type={"button"}
              variant="contained"
              color="error"
              onClick={deleteFile}
              sx={{ fontSize: 12 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}

      {keyName && errors && errors[keyName] ? (
        <FormHelperText error>
          {errors[keyName]?.message as string}
        </FormHelperText>
      ) : null}

      <Suspense fallback={<div>Loading...</div>}>
        <ImageCropper
          open={openImageCropper.open}
          onClose={(value: boolean, croppedFile?: File) =>
            setOpenImageCropper({ open: value, selectedValue: croppedFile })
          }
          selectedImage={openImageCropper.selectedValue}
          onCroped={(value: boolean, croppedFile?: File) =>
            HandleCroped(value, croppedFile)
          }
          cropWidth={cropWidth}
          cropHeight={cropHeight}
        />
      </Suspense>
    </Box>
  );
};

export default FileUploader;
